import type { Recipe } from '@recipes/contracts';
import { Badge, Button } from '@recipes/ui';
import { chosenHue } from '@recipes/domain';
import { AlertTriangle, Check, Palette } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { HUE_SURFACE } from '@/features/appearance/hue-classes';
import { TagColorsDialog } from '@/features/appearance/tag-colors-dialog';

export interface LabelsDisplayProps {
  recipe: Pick<Recipe, 'tags' | 'categories' | 'labels' | 'cuisine' | 'mealType'>;
  /** Confirm a suggested label (AI / import) in place. */
  onConfirmLabel?: (label: Recipe['labels'][number]) => void;
  confirming?: boolean;
  /** Set when the viewer may change the recipe: offers the tag colours. */
  onTagsChanged?: () => void;
}

function labelText(
  label: Recipe['labels'][number],
  t: (key: string, opts?: Record<string, unknown>) => string,
) {
  const key =
    label.kind === 'DIETARY'
      ? `recipes.labels.dietary.${label.value}`
      : `recipes.labels.allergen.${label.value}`;
  return t(key, { defaultValue: label.value.replace(/_/g, ' ') });
}

/** Tags, categories, cuisine/meal type and dietary/allergen labels; unconfirmed AI or import labels are flagged. */
export function LabelsDisplay({
  recipe,
  onConfirmLabel,
  confirming,
  onTagsChanged,
}: LabelsDisplayProps) {
  const { t } = useTranslation();
  const [coloursOpen, setColoursOpen] = useState(false);
  const hasAny =
    recipe.tags.length ||
    recipe.categories.length ||
    recipe.labels.length ||
    recipe.cuisine ||
    recipe.mealType;
  if (!hasAny) return null;
  return (
    <div className="space-y-2" data-testid="labels-display">
      {(recipe.categories.length > 0 || recipe.cuisine || recipe.mealType) && (
        <ul className="flex flex-wrap gap-1.5" aria-label={t('recipes.detail.categories')}>
          {recipe.categories.map((c) => (
            <li key={c.id}>
              <Badge variant="outline">
                <Link to={`/recipes?categoryIds=${c.id}`}>{c.path || c.name}</Link>
              </Badge>
            </li>
          ))}
          {recipe.cuisine && (
            <li>
              <Badge variant="outline">
                <Link to={`/recipes?cuisine=${encodeURIComponent(recipe.cuisine)}`}>
                  {recipe.cuisine}
                </Link>
              </Badge>
            </li>
          )}
          {recipe.mealType && (
            <li>
              <Badge variant="outline">
                <Link to={`/recipes?mealType=${encodeURIComponent(recipe.mealType)}`}>
                  {t(`recipes.mealType.${recipe.mealType}`, { defaultValue: recipe.mealType })}
                </Link>
              </Badge>
            </li>
          )}
        </ul>
      )}
      {recipe.tags.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label={t('recipes.detail.tags')}>
          {recipe.tags.map((tag) => (
            <li key={tag.id}>
              {/* The tag's hue – picked, else derived from its name; the name says what it is. */}
              <Badge
                variant="secondary"
                className={HUE_SURFACE[chosenHue(tag.color, tag.name)]}
                data-testid="recipe-tag"
              >
                <Link to={`/recipes?tags=${encodeURIComponent(tag.name)}`}>#{tag.name}</Link>
              </Badge>
            </li>
          ))}
          {onTagsChanged && (
            <li>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-1.5 text-xs text-muted-foreground"
                onClick={() => setColoursOpen(true)}
                data-testid="tag-colors-open"
              >
                <Palette className="size-3.5" aria-hidden /> {t('appearance.tagColors.action')}
              </Button>
              <TagColorsDialog
                open={coloursOpen}
                onOpenChange={setColoursOpen}
                tags={recipe.tags}
                onChanged={onTagsChanged}
              />
            </li>
          )}
        </ul>
      )}
      {recipe.labels.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label={t('recipes.detail.labels')}>
          {recipe.labels.map((label) => {
            const suggested = !label.confirmed && label.source !== 'USER';
            return (
              <li key={label.id} className="flex items-center gap-1">
                <Badge
                  variant={
                    label.kind === 'ALLERGEN' ? 'warning' : suggested ? 'outline' : 'success'
                  }
                  className={suggested ? 'border-dashed' : undefined}
                  data-testid={suggested ? 'label-suggested' : 'label-confirmed'}
                >
                  {label.kind === 'ALLERGEN' && (
                    <AlertTriangle className="mr-1 size-3" aria-hidden />
                  )}
                  {labelText(label, t)}
                  {suggested && (
                    <span className="ml-1 font-normal">
                      ·{' '}
                      {label.source === 'AI'
                        ? t('recipes.labels.aiSuggested')
                        : t('recipes.labels.importSuggested')}
                    </span>
                  )}
                </Badge>
                {suggested && onConfirmLabel && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs"
                    onClick={() => onConfirmLabel(label)}
                    disabled={confirming}
                    aria-label={`${t('recipes.labels.confirm')}: ${labelText(label, t)}`}
                  >
                    <Check aria-hidden /> {t('recipes.labels.confirm')}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
